<script>
import axios from '@/utils/axios';

const DEFAULT_FORM = () => ({
  employee_id: '',
  salary_structure_id: '',
  ctc_annual: '',
  effective_from: '',
});

export default {
  inject: ['setLoading'],
  emits: ['updated'],
  data() {
    return {
      mode: 'assign',          // 'assign' | 'revise'
      reviseEmployee: null,    // { id, name } when revising
      form: DEFAULT_FORM(),
      errors: {},
      employees: [],
      structures: [],
    };
  },
  computed: {
    monthlyPreview() {
      const annual = parseFloat(this.form.ctc_annual);
      if (isNaN(annual) || annual <= 0) return null;
      return (annual / 12).toLocaleString('en-IN', { maximumFractionDigits: 2 });
    },
    isRevise() {
      return this.mode === 'revise';
    },
  },
  mounted() {
    this.fetchEmployees();
    this.fetchStructures();
  },
  methods: {
    async fetchEmployees() {
      try {
        const res = await axios.get('/hrms/employees/dropdown');
        this.employees = res.data.data || [];
      } catch (error) {
        console.log(error.message);
      }
    },
    async fetchStructures() {
      try {
        const res = await axios.get('/hrms/salary-structures/dropdown');
        this.structures = res.data.data || [];
      } catch (error) {
        console.log(error.message);
      }
    },

    employeeLabel(e) {
      const name = e.full_name || `${e.first_name || ''} ${e.last_name || ''}`.trim() || e.name;
      return e.code ? `${name} (${e.code})` : name;
    },

    toInputDate(val) {
      if (!val) return '';
      return String(val).slice(0, 10);
    },

    // ----- open in ASSIGN mode -----
    assign() {
      this.mode = 'assign';
      this.reviseEmployee = null;
      this.resetForm();
    },

    // ----- open in REVISE mode (employee fixed) -----
    revise(row) {
      this.mode = 'revise';
      this.reviseEmployee = {
        id: row.employee_id,
        name: row.employee?.full_name || '-',
      };
      this.resetForm();
      this.form.employee_id = row.employee_id;
      // pre-fill current structure (optional to change)
      this.form.salary_structure_id = row.salary_structure_id || '';
    },

    buildPayload() {
      const payload = {
        ctc_annual: this.form.ctc_annual,
        effective_from: this.form.effective_from || null,
      };
      if (this.form.salary_structure_id) payload.salary_structure_id = this.form.salary_structure_id;
      if (!this.isRevise) payload.employee_id = this.form.employee_id || null;
      return payload;
    },

    async save() {
      this.errors = {};
      this.setLoading(true);
      try {
        let response;
        if (this.isRevise) {
          response = await axios.put(`/hrms/employee-salaries/${this.form.employee_id}/revise`, this.buildPayload());
        } else {
          response = await axios.post('/hrms/employee-salaries', this.buildPayload());
        }
        this.$swalToast(response.data.message);
        this.$emit('updated');
        this.$closeModal('hrm_employee_salary_modal');
        this.resetForm();
      } catch (error) {
        if (error.type === 'validation') {
          this.errors = error.errors;
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },

    resetForm() {
      const keepEmployee = this.isRevise ? this.form.employee_id : '';
      const keepStructure = this.isRevise ? this.form.salary_structure_id : '';
      this.form = DEFAULT_FORM();
      if (this.isRevise) {
        this.form.employee_id = keepEmployee;
        this.form.salary_structure_id = keepStructure;
      }
      this.errors = {};
    },
  },
};
</script>

<template>
  <div class="modal fade" id="hrm_employee_salary_modal">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">
            {{ isRevise ? 'Revise Salary' : 'Assign Salary' }}
            <span v-if="isRevise && reviseEmployee" style="font-size:13px; color:#94a3b8; font-weight:500;">— {{ reviseEmployee.name }}</span>
          </h4>
          <button type="button" class="btn-close custom-btn-close" data-bs-dismiss="modal" aria-label="Close">
            <i class="ti ti-x"></i>
          </button>
        </div>
        <form @submit.prevent="save">
          <div class="modal-body pb-0">

            <!-- Revise note -->
            <div v-if="isRevise" class="es-note mb-3">
              <i class="ti ti-info-circle me-1"></i>
              Revising creates a new salary record and closes the current one. Leave structure unchanged to keep the existing one.
            </div>

            <!-- Employee (only in assign) -->
            <div v-if="!isRevise" class="mb-3">
              <label class="form-label">Employee <span class="text-danger">*</span></label>
              <select class="form-select" v-model="form.employee_id">
                <option value="">Select employee</option>
                <option v-for="e in employees" :key="e.id" :value="e.id">{{ employeeLabel(e) }}</option>
              </select>
              <div v-if="errors.employee_id" class="text-danger small mt-1">{{ errors.employee_id[0] }}</div>
            </div>

            <!-- Salary Structure -->
            <div class="mb-3">
              <label class="form-label">
                Salary Structure
                <span v-if="!isRevise" class="text-danger">*</span>
                <span v-else style="color:#94a3b8; font-weight:400;">(optional — keep current)</span>
              </label>
              <select class="form-select" v-model="form.salary_structure_id">
                <option value="">{{ isRevise ? 'Keep current structure' : 'Select structure' }}</option>
                <option v-for="s in structures" :key="s.id" :value="s.id">{{ s.name }}</option>
              </select>
              <div v-if="errors.salary_structure_id" class="text-danger small mt-1">{{ errors.salary_structure_id[0] }}</div>
            </div>

            <div class="row">
              <!-- CTC Annual -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Annual CTC <span class="text-danger">*</span></label>
                <div class="input-group">
                  <span class="input-group-text">₹</span>
                  <input type="number" step="0.01" min="1" class="form-control" v-model="form.ctc_annual" placeholder="e.g. 600000" />
                </div>
                <small v-if="monthlyPreview" class="text-muted" style="font-size:10.5px;">
                  Monthly: ₹{{ monthlyPreview }}
                </small>
                <div v-if="errors.ctc_annual" class="text-danger small mt-1">{{ errors.ctc_annual[0] }}</div>
              </div>

              <!-- Effective From -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Effective From <span class="text-danger">*</span></label>
                <input type="date" class="form-control" v-model="form.effective_from" />
                <div v-if="errors.effective_from" class="text-danger small mt-1">{{ errors.effective_from[0] }}</div>
              </div>
            </div>

          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal">Cancel</button>
            <button type="submit" class="btn btn-primary">{{ isRevise ? 'Revise' : 'Assign' }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<style scoped>
.es-note {
  font-size: 11.5px; color: #92722a;
  background: #fffbeb; border: 1px solid #fde68a;
  border-radius: 8px; padding: 9px 12px; line-height: 1.5;
}
</style>